import { SFX, type Channel, type SfxName, type Shape } from "./sfx.js";

/**
 * All sound goes through here.
 *
 * One `AudioContext`, created on the first real gesture because browsers refuse
 * to start one otherwise. Everything is synthesised from the manifest, so there
 * is nothing to preload and no silent failure mode where a missing file means a
 * missing cue.
 *
 * Volumes live in localStorage rather than in React state: the bus is used from
 * the event scheduler, which has no component to read from.
 */

export interface AudioSettings {
  readonly muted: boolean;
  readonly sfx: number;
  readonly ui: number;
  readonly music: number;
}

const STORAGE_KEY = "guandan.audio";

export const DEFAULT_SETTINGS: AudioSettings = { muted: false, sfx: 0.8, ui: 0.6, music: 0.5 };

export function loadSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      muted: parsed.muted ?? DEFAULT_SETTINGS.muted,
      sfx: clamp(parsed.sfx ?? DEFAULT_SETTINGS.sfx),
      ui: clamp(parsed.ui ?? DEFAULT_SETTINGS.ui),
      music: clamp(parsed.music ?? DEFAULT_SETTINGS.music),
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
}

export class AudioBus {
  #ctx: AudioContext | null = null;
  #master: GainNode | null = null;
  #channels = new Map<Channel, GainNode>();
  #settings: AudioSettings = DEFAULT_SETTINGS;
  #lastPlayed = new Map<SfxName, number>();
  #noise: AudioBuffer | null = null;
  #speechBuffers = new Map<string, AudioBuffer>();
  #speechLoads = new Map<string, Promise<AudioBuffer | null>>();
  #speechSource: AudioBufferSourceNode | null = null;
  #speechDuck: GainNode | null = null;
  #speechPending = new Set<string>();

  constructor() {
    if (typeof localStorage !== "undefined") this.#settings = loadSettings();
  }

  get settings(): AudioSettings { return this.#settings; }
  get started(): boolean { return this.#ctx !== null; }

  /**
   * Start the audio engine. Safe to call on every gesture; only the first does
   * anything, and a browser that refuses is simply left silent.
   */
  unlock(): void {
    if (this.#ctx !== null) {
      if (this.#ctx.state === "suspended") void this.#ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (Ctor === undefined) return;
    try {
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = this.#settings.muted ? 0 : 1;
      master.connect(ctx.destination);
      this.#speechDuck = ctx.createGain();
      this.#speechDuck.connect(master);
      for (const channel of ["sfx", "ui", "music"] as Channel[]) {
        const gain = ctx.createGain();
        gain.gain.value = this.#settings[channel];
        gain.connect(channel === "music" ? this.#speechDuck : master);
        this.#channels.set(channel, gain);
      }
      this.#ctx = ctx;
      this.#master = master;
      for (const assetId of this.#speechPending) void this.preloadSpeech(assetId);
      this.#speechPending.clear();
    } catch {
      // No audio available. Everything below becomes a no-op.
    }
  }

  update(patch: Partial<AudioSettings>): AudioSettings {
    this.#settings = { ...this.#settings, ...patch };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.#settings));
    } catch {
      // Private browsing; the session still honours the setting.
    }
    if (this.#settings.muted) this.stopSpeech();
    if (this.#master !== null) this.#master.gain.value = this.#settings.muted ? 0 : 1;
    for (const [channel, node] of this.#channels) node.gain.value = this.#settings[channel];
    return this.#settings;
  }

  play(name: SfxName): void {
    const ctx = this.#ctx;
    if (ctx === null || this.#settings.muted) return;
    const slot = SFX[name];

    // Throttled cues — the XP tick, the clock — would otherwise stack into a
    // buzz when many land in the same frame.
    const throttle = slot.throttleMs;
    if (throttle !== undefined) {
      const last = this.#lastPlayed.get(name) ?? 0;
      if (ctx.currentTime * 1000 - last < throttle) return;
      this.#lastPlayed.set(name, ctx.currentTime * 1000);
    }

    const shape = slot.shapes[Math.floor(Math.random() * slot.shapes.length)];
    if (shape === undefined) return;
    const target = this.#channels.get(slot.channel);
    if (target === undefined) return;
    // Keep the attack crisp; duck only the sustained impact beneath a voice.
    if (this.#speechSource !== null && shape.kind === "impact") {
      const tail = ctx.createGain();
      tail.gain.setTargetAtTime(0.6, ctx.currentTime + 0.08, 0.025);
      tail.connect(target);
      this.#render(ctx, shape, tail, ctx.currentTime);
    } else this.#render(ctx, shape, target, ctx.currentTime);
  }

  /** Prefetch a server-selected immutable MP3; failures are intentionally silent. */
  async preloadSpeech(assetId: string): Promise<void> {
    if (this.#speechBuffers.has(assetId)) return;
    if (this.#ctx === null) {
      this.#speechPending.add(assetId);
      return;
    }
    const existing = this.#speechLoads.get(assetId);
    if (existing !== undefined) { await existing; return; }
    const load = (async () => {
      try {
        const response = await fetch(`/api/voice/${assetId}.mp3`);
        if (!response.ok) return null;
        const bytes = await response.arrayBuffer();
        const decoded = await this.#ctx?.decodeAudioData(bytes.slice(0));
        if (decoded !== undefined && decoded.duration <= 1.4) this.#speechBuffers.set(assetId, decoded);
        return decoded ?? null;
      } catch {
        return null;
      }
    })();
    this.#speechLoads.set(assetId, load);
    try { await load; } finally { this.#speechLoads.delete(assetId); }
  }

  /** Play only if the cue arrived in time; a cold miss never plays late. */
  playSpeech(assetId: string): boolean {
    const ctx = this.#ctx;
    const channel = this.#channels.get("sfx");
    const buffer = this.#speechBuffers.get(assetId);
    if (ctx === null || channel === undefined || buffer === undefined || this.#settings.muted || this.#speechSource !== null) return false;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(channel);
    this.#speechSource = source;
    this.#speechDuck?.gain.setTargetAtTime(0.55, ctx.currentTime, 0.035);
    source.onended = () => {
      if (this.#speechSource !== source) return;
      this.#speechSource = null;
      this.#speechDuck?.gain.setTargetAtTime(1, ctx.currentTime, 0.08);
      source.disconnect();
    };
    source.start();
    return true;
  }

  stopSpeech(): void {
    const source = this.#speechSource;
    this.#speechSource = null;
    if (source !== null) { source.onended = null; source.stop(); source.disconnect(); }
    if (this.#ctx !== null) this.#speechDuck?.gain.setTargetAtTime(1, this.#ctx.currentTime, 0.035);
  }

  #render(ctx: AudioContext, shape: Shape, out: GainNode, at: number): void {
    switch (shape.kind) {
      case "noise": {
        const source = ctx.createBufferSource();
        source.buffer = this.#noiseBuffer(ctx);
        const filter = ctx.createBiquadFilter();
        filter.type = "lowpass";
        filter.frequency.setValueAtTime(shape.cutoff, at);
        if (shape.sweep !== undefined) {
          filter.frequency.exponentialRampToValueAtTime(
            Math.max(120, shape.cutoff * (1 + shape.sweep)), at + shape.duration,
          );
        }
        const env = ctx.createGain();
        env.gain.setValueAtTime(shape.gain ?? 0.5, at);
        env.gain.exponentialRampToValueAtTime(0.0001, at + shape.duration);
        source.connect(filter).connect(env).connect(out);
        source.start(at);
        source.stop(at + shape.duration);
        return;
      }
      case "tone": {
        this.#tone(ctx, out, at, shape.freq, shape.to ?? shape.freq, shape.duration, shape.type ?? "sine", shape.gain ?? 0.25);
        return;
      }
      case "sequence": {
        for (const step of shape.steps) {
          this.#tone(ctx, out, at + step.at, step.freq, step.freq, step.duration, step.type ?? "triangle", shape.gain ?? 0.4);
        }
        return;
      }
      case "impact": {
        // Low body for the thump, noise for the debris.
        this.#tone(ctx, out, at, shape.freq, shape.freq * 0.35, shape.duration, "sine", shape.gain ?? 0.7);
        this.#render(
          ctx,
          { kind: "noise", duration: shape.duration * 0.75, cutoff: 3200, sweep: -0.85, gain: shape.noise },
          out, at,
        );
        return;
      }
    }
  }

  #tone(
    ctx: AudioContext, out: GainNode, at: number,
    from: number, to: number, duration: number, type: OscillatorType, gain: number,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(from, at);
    if (to !== from) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), at + duration);
    const env = ctx.createGain();
    // A tiny attack, because an instant one clicks.
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(gain, at + 0.008);
    env.gain.exponentialRampToValueAtTime(0.0001, at + duration);
    osc.connect(env).connect(out);
    osc.start(at);
    osc.stop(at + duration + 0.02);
  }

  /** One second of white noise, reused by every card sound. */
  #noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.#noise !== null) return this.#noise;
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    this.#noise = buffer;
    return buffer;
  }
}

/** One bus for the whole app. */
export const audio = new AudioBus();
